import { warn } from '@execonline-inc/logging';
import { assertNever } from '@kofno/piper';
import { fromNullable, Maybe } from 'maybeasy';

type ConsentValue = 'ACCEPT' | 'DENY';

// Osano reports a value for every category it knows (ANALYTICS, MARKETING, STORAGE,
// OPT_OUT and others) and can add more, so the category keys stay open.
type ConsentState = Record<string, ConsentValue>;

interface OsanoConsentManager {
  mode: 'debug' | 'permissive' | 'production';
  getConsent: () => ConsentState;
  showDrawer: (className: string) => void;
  addEventListener: (
    event: 'osano-cm-consent-changed' | 'osano-cm-consent-saved',
    callback: (consent: ConsentState) => void,
  ) => void;
}

declare global {
  interface Window {
    Osano?: { cm: OsanoConsentManager };
  }
}

type ListenerState = { kind: 'watching'; previous: ConsentState } | { kind: 'withdrawn' };

const watching = (previous: ConsentState): ListenerState => ({ kind: 'watching', previous });
const withdrawn = (): ListenerState => ({ kind: 'withdrawn' });

const withdraws = (previous: ConsentState, change: ConsentState): boolean =>
  Object.entries(change).some(
    ([category, value]) => value === 'DENY' && previous[category] === 'ACCEPT',
  );

const listenForWithdrawal = (cm: OsanoConsentManager): void => {
  let state = watching(cm.getConsent());

  cm.addEventListener('osano-cm-consent-changed', (change) => {
    switch (state.kind) {
      case 'watching': {
        if (withdraws(state.previous, change)) {
          state = withdrawn();
        }
        break;
      }
      case 'withdrawn': {
        break;
      }
      default: {
        assertNever(state);
      }
    }
  });

  cm.addEventListener('osano-cm-consent-saved', (consent) => {
    switch (state.kind) {
      case 'watching': {
        state = watching(consent);
        break;
      }
      case 'withdrawn': {
        globalThis.location.reload();
        break;
      }
      default: {
        assertNever(state);
      }
    }
  });
};

// Osano stops scripts from loading once consent is withdrawn, but it cannot stop code
// that already ran. This app never reloads on its own, so reload when a visitor takes
// back consent they had given. osano.js loads before this bundle, so window.Osano is
// already set when this runs. Ad blockers often block osano.js, so a missing consent
// manager is a warning, not an error report.
export const reloadOnConsentWithdrawal = (): void => {
  fromNullable(window.Osano)
    .map(({ cm }) => cm)
    .do(listenForWithdrawal)
    .elseDo(() => {
      warn('Osano consent manager did not load');
    });
};

// Osano blocks new cookies for a denied category, but it does not remove the ones a
// visitor got before they denied it. Google Analytics and Intercom write theirs to the
// parent domain, so expire each name on the host and on every parent domain. These
// prefixes match the cookie rules in the Osano configuration.
const deniedCookiePrefixes: Record<string, ReadonlyArray<string>> = {
  ANALYTICS: ['_ga'],
  PERSONALIZATION: ['intercom-id-', 'intercom-session-', 'intercom-device-id-'],
};

const cookieNames = (): ReadonlyArray<string> =>
  document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => name.length > 0);

const cookieDomains = (hostname: string): ReadonlyArray<string> => {
  const labels = hostname.split('.');
  return labels.slice(0, -1).map((_, index) => labels.slice(index).join('.'));
};

const expireCookie = (name: string): void => {
  const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  document.cookie = expired;
  cookieDomains(globalThis.location.hostname).forEach((domain) => {
    document.cookie = `${expired}; domain=${domain}`;
  });
};

const removeCookiesForDeniedCategories = (cm: OsanoConsentManager): void => {
  const consent = cm.getConsent();
  const prefixes = Object.entries(deniedCookiePrefixes)
    .filter(([category]) => consent[category] === 'DENY')
    .flatMap(([, categoryPrefixes]) => categoryPrefixes);

  cookieNames()
    .filter((name) => prefixes.some((prefix) => name.startsWith(prefix)))
    .forEach(expireCookie);
};

// Osano reports Discovery/Listener mode as 'debug'. In that mode it shows no banner and
// blocks nothing, and OneTrust is still the consent tool, so preferences stay hidden.
export const enforcingConsentManager = (): Maybe<OsanoConsentManager> =>
  fromNullable(window.Osano)
    .map(({ cm }) => cm)
    .filter((cm) => cm.mode !== 'debug');

export const showCookiePreferences = (cm: OsanoConsentManager): void => {
  cm.showDrawer('osano-cm-dom-info-dialog-open');
};

// A withdrawal reloads the page, so this runs again right after one. It also covers a
// withdrawal made in another app on the same parent domain.
export const removeDeniedCookies = (): void => {
  enforcingConsentManager().do(removeCookiesForDeniedCategories);
};
